import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type Lang,
  type Translations,
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  translations,
  normalizeLangCode,
  parseLangFromPath,
  langPrefix,
  langMeta,
} from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import GeoRecommendBanner from '@/components/GeoRecommendBanner';
import { GEO_COUNTRY_KEY, readStoredGeoCountry } from '@/data/countryPricing';

interface I18nContextValue {
  lang: Lang;
  t: Translations;
  /** Build a localized path: localePath('/test/lc') → '/ja/test/lc' */
  localePath: (path: string) => string;
  /** ISO country from detect-country (IP). Used for checkout wallets/currency. */
  geoCountry: string | null;
}

const I18nContext = createContext<I18nContextValue>({
  lang: DEFAULT_LANG,
  t: translations[DEFAULT_LANG],
  localePath: (p) => p,
  geoCountry: null,
});

export const useI18n = () => useContext(I18nContext);

const GEO_STORAGE_KEY = 'pt_geo_lang';
const LANG_STORAGE_KEY = 'pt_lang';
const AUTO_LANG_KEY = 'pt_auto_lang_done';

const HANGUL_RE = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/;
const HANGUL_RE_GLOBAL = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]+/g;

/** Strip accidental Korean copy in non-ko locale strings only (no English fallback). */
function sanitizeTranslationValue(current: unknown, _english: unknown, lang: Lang): unknown {
  if (typeof current === 'string') {
    if (lang !== 'ko' && HANGUL_RE.test(current)) {
      return current.replace(HANGUL_RE_GLOBAL, '').replace(/\s{2,}/g, ' ').trim() || current;
    }
    return current;
  }

  if (Array.isArray(current)) {
    return current.map((item) => sanitizeTranslationValue(item, undefined, lang));
  }

  if (current && typeof current === 'object') {
    const currentObj = current as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(currentObj)) {
      out[key] = sanitizeTranslationValue(currentObj[key], undefined, lang);
    }
    return out;
  }

  return current;
}

function resolveInitialLang(pathname: string): Lang {
  return parseLangFromPath(pathname);
}

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [lang, setLang] = useState<Lang>(() => resolveInitialLang(pathname));
  const t = useMemo(() => {
    const current = translations[lang];
    return sanitizeTranslationValue(current, undefined, lang) as Translations;
  }, [lang]);
  const didGeoDetect = useRef(false);
  const [recommendedLang, setRecommendedLang] = useState<Lang | null>(null);
  const [geoCountry, setGeoCountry] = useState<string | null>(() => readStoredGeoCountry());

  useEffect(() => {
    const nextLang = parseLangFromPath(pathname);

    setLang(nextLang);
    document.documentElement.lang = langMeta[nextLang].htmlLang;
    document.documentElement.dir = nextLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('data-lang', nextLang);

    if (nextLang !== DEFAULT_LANG) {
      localStorage.setItem(LANG_STORAGE_KEY, nextLang);
      localStorage.setItem(GEO_STORAGE_KEY, nextLang);
      setRecommendedLang(null);
    }
  }, [pathname]);

  // Runtime fail-safe: never show Hangul outside Korean locale.
  useEffect(() => {
    if (lang === 'ko') return;

    const sanitizeTextNodes = (root: Node | null) => {
      if (!root) return;
      const doc = root.ownerDocument ?? document;
      const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const touched = new Set<Node>();

      let node = walker.nextNode();
      while (node) {
        const parentTag = (node.parentElement?.tagName || '').toLowerCase();
        if (parentTag !== 'script' && parentTag !== 'style') {
          const current = node.nodeValue || '';
          if (HANGUL_RE.test(current)) {
            const next = current.replace(HANGUL_RE_GLOBAL, '').replace(/\s{2,}/g, ' ').trim();
            node.nodeValue = next;
            touched.add(node.parentNode || node);
          }
        }
        node = walker.nextNode();
      }

      for (const item of touched) {
        if (item instanceof HTMLElement && item.textContent?.trim() === '') {
          item.style.display = 'none';
        }
      }
    };

    sanitizeTextNodes(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => sanitizeTextNodes(n));
        if (m.type === 'characterData') sanitizeTextNodes(m.target);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [lang]);

  // Auto-redirect once per session: non-Korean browsers landing on the Korean root
  // get sent to their own locale prefix. Bots are excluded so canonical/hreflang stay intact.
  useEffect(() => {
    if (lang !== DEFAULT_LANG) return;
    if (typeof window === 'undefined') return;

    const ua = navigator.userAgent || '';
    const isBot = /bot|crawl|spider|slurp|bingpreview|headlesschrome|lighthouse|gtmetrix|pagespeed/i.test(ua);
    if (isBot) return;

    try {
      if (sessionStorage.getItem(AUTO_LANG_KEY)) return;
      const explicit = localStorage.getItem(LANG_STORAGE_KEY);
      if (explicit) return;

      const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
      let target: Lang | null = null;
      for (const raw of candidates) {
        const normalized = normalizeLangCode(raw);
        if (normalized && SUPPORTED_LANGS.includes(normalized)) {
          target = normalized;
          break;
        }
      }

      sessionStorage.setItem(AUTO_LANG_KEY, '1');
      if (!target || target === DEFAULT_LANG) return;

      const rest = pathname === '/' ? '' : pathname;
      window.location.replace(`/${target}${rest}${window.location.search}${window.location.hash}`);
    } catch {
      /* storage unavailable — skip auto-redirect */
    }
  }, [lang, pathname]);

  // Always persist IP country for checkout. Banner only on Korean default UI.
  useEffect(() => {
    if (didGeoDetect.current) return;
    didGeoDetect.current = true;

    supabase.functions.invoke('detect-country').then(({ data }) => {
      const cc = String(data?.country || '').toUpperCase();
      if (/^[A-Z]{2}$/.test(cc) && cc !== 'XX') {
        try {
          localStorage.setItem(GEO_COUNTRY_KEY, cc);
        } catch {
          /* storage unavailable */
        }
        setGeoCountry(cc);
      }

      const pathSegments = pathname.split('/').filter(Boolean);
      const currentSegment = pathSegments.length > 0 ? normalizeLangCode(pathSegments[0]) : null;
      const hasLangPrefix = !!currentSegment && SUPPORTED_LANGS.includes(currentSegment) && currentSegment !== DEFAULT_LANG;
      if (hasLangPrefix || lang !== DEFAULT_LANG) {
        setRecommendedLang(null);
        return;
      }

      const stored = localStorage.getItem(LANG_STORAGE_KEY) || localStorage.getItem(GEO_STORAGE_KEY);
      if (stored) {
        const normalizedStored = normalizeLangCode(stored);
        if (normalizedStored && normalizedStored !== DEFAULT_LANG && SUPPORTED_LANGS.includes(normalizedStored) && normalizedStored !== lang) {
          setRecommendedLang(normalizedStored);
        }
        return;
      }

      const detectedLang = normalizeLangCode(data?.lang);
      if (detectedLang && SUPPORTED_LANGS.includes(detectedLang)) {
        localStorage.setItem(GEO_STORAGE_KEY, detectedLang);
        if (detectedLang !== lang) {
          setRecommendedLang(detectedLang);
        }
      } else {
        localStorage.setItem(GEO_STORAGE_KEY, DEFAULT_LANG);
      }
    }).catch(() => {
      try {
        localStorage.setItem(GEO_STORAGE_KEY, DEFAULT_LANG);
      } catch {
        /* storage unavailable */
      }
    });
  }, [pathname, lang]);

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      t,
      geoCountry,
      localePath: (path: string) => `${langPrefix(lang)}${path}`,
    }),
    [lang, t, geoCountry],
  );

  return (
    <I18nContext.Provider value={value}>
      {recommendedLang && <GeoRecommendBanner recommendedLang={recommendedLang} currentLang={lang} />}
      {children}
    </I18nContext.Provider>
  );
};
